import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { Check, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Pagination } from '@/components/Pagination'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useWebFormLeads, useConvertToLead, useMarkWebFormConverted } from '@/hooks/useWebForms'
import { leadKeys } from '@/hooks/useLeads'
import { useAuthStore } from '@/stores/authStore'
import { leadService } from '@/api/leadService'
import { WebFormStatus } from '@/types/api'
import type { DuplicateCheckResponse, LeadWebFilterParams, LeadWebResponse } from '@/types/api'

function buildWebFormNote(item: LeadWebResponse): string {
  const lines: string[] = [
    `[Web Form Submission — ${item.form_type} — ${new Date(item.created_at).toLocaleDateString()}]`,
  ]
  if (item.email)        lines.push(`Email: ${item.email}`)
  if (item.phone)        lines.push(`Phone: ${item.phone}`)
  if (item.city)         lines.push(`City: ${item.city}`)
  if (item.zip)          lines.push(`Zip: ${item.zip}`)
  if (item.project)      lines.push(`Project: ${item.project}`)
  if (item.sqft)         lines.push(`Sqft: ${item.sqft}`)
  if (item.comment)      lines.push(`Comment: ${item.comment}`)
  if (item.news_consent != null) lines.push(`Newsletter consent: ${item.news_consent ? 'Yes' : 'No'}`)
  if (item.data_consent != null) lines.push(`Data consent: ${item.data_consent ? 'Yes' : 'No'}`)

  return lines.join('\n')
}

function WebFormTable({
  params,
  showConvertAction,
}: {
  params: LeadWebFilterParams
  showConvertAction: boolean
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<LeadWebFilterParams>({
    limit: 25,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  })
  const { data, isLoading } = useWebFormLeads(filters)
  const convertMutation = useConvertToLead()
  const markConvertedMutation = useMarkWebFormConverted()
  const { user } = useAuthStore()

  const [duplicateData, setDuplicateData] = useState<DuplicateCheckResponse | null>(null)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [pendingConversion, setPendingConversion] = useState<{ id: string; assignedTo: string } | null>(null)
  const [pendingWebFormItem, setPendingWebFormItem] = useState<LeadWebResponse | null>(null)
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false)
  const [isMerging, setIsMerging] = useState(false)

  const handleConvert = async (item: LeadWebResponse) => {
    if (!user) return
    const firstName = item.first_name ?? item.full_name?.split(' ')[0] ?? ''
    const lastName = item.last_name ?? item.full_name?.split(' ').slice(1).join(' ') ?? ''
    const assignedTo = user.id

    setIsCheckingDuplicate(true)
    try {
      const result = await leadService.checkDuplicate({
        first_name: firstName,
        last_name: lastName,
        email: item.email,
        phone: item.phone,
      })
      if (result.is_duplicate) {
        setPendingConversion({ id: item.id, assignedTo })
        setPendingWebFormItem(item)
        setDuplicateData(result)
        setShowDuplicateDialog(true)
      } else {
        convertMutation.mutate({ id: item.id, assignedTo })
      }
    } catch {
      convertMutation.mutate({ id: item.id, assignedTo })
    } finally {
      setIsCheckingDuplicate(false)
    }
  }

  const handleMergeDetails = async () => {
    if (!duplicateData?.lead || !pendingWebFormItem) return
    setIsMerging(true)
    try {
      const existing = await leadService.getLead(duplicateData.lead.id)
      const webFormNote = buildWebFormNote(pendingWebFormItem)
      const mergedNotes = existing.notes
        ? `${existing.notes}\n\n${webFormNote}`
        : webFormNote
      const updatedLead = await leadService.updateLead(duplicateData.lead.id, { notes: mergedNotes })
      queryClient.setQueryData(leadKeys.detail(duplicateData.lead.id), updatedLead)
      await markConvertedMutation.mutateAsync({
        id: pendingWebFormItem.id,
        convertedLeadId: duplicateData.lead.id,
      })
      setShowDuplicateDialog(false)
      navigate(`/leads/${duplicateData.lead.id}`)
    } finally {
      setIsMerging(false)
    }
  }

  const handleCreateDuplicate = () => {
    setShowDuplicateDialog(false)
    if (pendingConversion) {
      convertMutation.mutate(pendingConversion)
    }
  }

  const getDisplayName = (item: LeadWebResponse) => {
    if (item.full_name) return item.full_name
    if (item.first_name || item.last_name) {
      return `${item.first_name || ''} ${item.last_name || ''}`.trim()
    }
    return '-'
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="hidden md:table-cell">Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead className="hidden md:table-cell">Project</TableHead>
            <TableHead className="hidden md:table-cell">Consent</TableHead>
            <TableHead className="hidden md:table-cell">Created</TableHead>
            {showConvertAction && <TableHead>Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">
                {getDisplayName(item)}
              </TableCell>
              <TableCell className="hidden md:table-cell">{item.email}</TableCell>
              <TableCell>{item.phone || '-'}</TableCell>
              <TableCell className="hidden md:table-cell">{item.project || '-'}</TableCell>
              <TableCell className="hidden md:table-cell">
                {item.data_consent ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">
                {format(new Date(item.created_at), 'MMM d, yyyy')}
              </TableCell>
              {showConvertAction && (
                <TableCell>
                  {item.status !== WebFormStatus.CONVERTED ? (
                    <Button
                      size="sm"
                      onClick={() => handleConvert(item)}
                      disabled={convertMutation.isPending || isCheckingDuplicate}
                    >
                      Create Lead
                    </Button>
                  ) : (
                    <Badge variant="secondary">Converted</Badge>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {(!data?.items || data.items.length === 0) && (
            <TableRow>
              <TableCell
                colSpan={showConvertAction ? 7 : 6}
                className="text-center text-muted-foreground"
              >
                No form submissions found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Pagination
        total={data?.total || 0}
        limit={filters.limit || 25}
        offset={filters.offset || 0}
        onPageChange={(offset) =>
          setFilters((prev) => ({ ...prev, offset }))
        }
        onLimitChange={(limit) =>
          setFilters((prev) => ({ ...prev, limit, offset: 0 }))
        }
      />

      {duplicateData?.lead && (
        <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Duplicate Contact Detected</DialogTitle>
              <DialogDescription>
                This contact has been identified as existing, please input the details in
                the first registration of this contact or accept the creation of a duplicate.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm font-medium">The following fields match an existing contact:</p>
              <ul className="text-sm space-y-1">
                {duplicateData.matched_fields.includes('name') && (
                  <li>
                    <span className="font-medium">Name: </span>
                    {duplicateData.lead.first_name} {duplicateData.lead.last_name}
                  </li>
                )}
                {duplicateData.matched_fields.includes('email') && (
                  <li>
                    <span className="font-medium">Email: </span>
                    {duplicateData.lead.email}
                  </li>
                )}
                {duplicateData.matched_fields.includes('phone') && (
                  <li>
                    <span className="font-medium">Phone: </span>
                    {duplicateData.lead.phone}
                  </li>
                )}
              </ul>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={handleCreateDuplicate}
                disabled={convertMutation.isPending || isMerging}
              >
                {convertMutation.isPending ? 'Converting...' : 'Create a duplicate'}
              </Button>
              <Button
                onClick={handleMergeDetails}
                disabled={isMerging || convertMutation.isPending}
              >
                {isMerging ? 'Merging...' : 'Merge details'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export function FilledFormsPage() {
  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Filled Forms</h1>
        <p className="text-muted-foreground">
          Website contact form submissions
        </p>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="submitted">Submitted</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardContent className="pt-6">
              <WebFormTable
                params={{ status: WebFormStatus.NEW }}
                showConvertAction
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="submitted">
          <Card>
            <CardContent className="pt-6">
              <WebFormTable params={{}} showConvertAction={false} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
